// Desktop header nav as a slow back-and-forth, swipeable strip between logo and icons.
// - Only activates when the links overflow the available width; otherwise links sit centred.
// - Drifts back and forth (ping-pong) with a short rest at each end; no clones or looping.
// - Pauses on hover, keyboard focus and hidden tabs; reduced motion keeps it manual (drag/trackpad).
// - Mouse drag scrolls; a drag never triggers the link underneath.
class SNavMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-nav-track]');
    this.nav = this.track?.querySelector('[data-desktop-nav]');
    this.list = this.nav?.querySelector(':scope > ul');
    if (!this.track || !this.list) return;

    this.speed = 11; // px per second (halved Oct 21 at merchant request)
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.touchHold = false;
    this.hovering = false;
    this.focused = false;
    this.dragging = false;
    this.dragMoved = false;
    this.lastTime = 0;

    this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.hovering = true; });
    this.addEventListener('pointerleave', () => { this.hovering = false; });
    this.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) this.focused = true; });
    this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.focused = false; });

    this.track.addEventListener('scroll', () => this.wrap(), { passive: true });
    this.track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || !this.active) return;
      this.dragging = true;
      this.dragMoved = false;
      this.dragStartX = e.clientX;
      this.dragLastX = e.clientX;
    });
    this.onMove = (e) => {
      if (!this.dragging) return;
      if (Math.abs(e.clientX - this.dragStartX) > 5) this.dragMoved = true;
      if (!this.dragMoved) return;
      this.track.scrollLeft -= e.clientX - this.dragLastX;
      this.dragLastX = e.clientX;
      this.position = this.track.scrollLeft;
    };
    this.onUp = () => { this.dragging = false; };
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
    this.track.addEventListener('click', (e) => {
      if (!this.dragMoved) return;
      e.preventDefault();
      e.stopPropagation();
      this.dragMoved = false;
    }, true);

    this.resizeObserver = new ResizeObserver(() => this.evaluate());
    this.resizeObserver.observe(this);
    this.track.addEventListener('touchstart', () => { this.touchHold = true; clearTimeout(this.touchTimer); }, { passive: true });
    this.track.addEventListener('touchend', () => { clearTimeout(this.touchTimer); this.touchTimer = setTimeout(() => { this.touchHold = false; }, 2500); }, { passive: true });
    this.evaluate();
    this.frame = requestAnimationFrame((t) => this.tick(t));
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
  }

  // Ping-pong: drift toward the end, rest briefly, then drift back. No clones, no looping.
  evaluate() {
    this.nav.querySelectorAll(':scope > [data-nav-clone]').forEach((c) => c.remove());
    const overflows = this.offsetParent !== null && this.nav.scrollWidth > this.track.clientWidth + 1;
    this.setActive(overflows);
    if (overflows) {
      this.direction = this.direction || 1;
      this.position = this.track.scrollLeft;
    }
  }

  setActive(active) {
    this.active = active;
    this.toggleAttribute('data-active', active);
    if (!active) this.track.scrollLeft = 0;
    this.atStart = undefined;
    this.updateEdges();
  }

  // Fade only the side that still hides links.
  updateEdges() {
    const max = this.maxScroll();
    const position = Math.abs(this.track.scrollLeft);
    const atStart = !this.active || position <= 2;
    const atEnd = !this.active || position >= max - 2;
    if (atStart === this.atStart && atEnd === this.atEnd) return;
    this.atStart = atStart;
    this.atEnd = atEnd;
    this.toggleAttribute('data-at-start', atStart);
    this.toggleAttribute('data-at-end', atEnd);
    const left = atStart ? '0px' : '12%';
    const right = atEnd ? '0px' : '12%';
    const mask = this.active
      ? `linear-gradient(to right, transparent 0, black ${left}, black calc(100% - ${right}), transparent 100%)`
      : '';
    this.track.style.maskImage = mask;
    this.track.style.webkitMaskImage = mask;
  }

  maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  // Keep the drift in sync after manual scrolling.
  wrap() {
    this.updateEdges();
    if (!this.active) return;
    if (Math.abs(this.track.scrollLeft - this.position) > 2) this.position = this.track.scrollLeft;
  }

  tick(time) {
    const delta = this.lastTime ? Math.min(time - this.lastTime, 64) : 0;
    this.lastTime = time;
    const resting = time < (this.restUntil || 0);
    const paused = !this.active || resting || this.touchHold || this.hovering || this.focused || this.dragging || document.hidden || this.reducedMotion.matches;
    if (!paused && this.position !== undefined) {
      const max = this.maxScroll();
      this.position += (this.direction * this.speed * delta) / 1000;
      if (this.position >= max) {
        this.position = max;
        this.direction = -1;
        this.restUntil = time + 1200;
      } else if (this.position <= 0) {
        this.position = 0;
        this.direction = 1;
        this.restUntil = time + 1200;
      }
      this.track.scrollLeft = this.position;
      this.updateEdges();
    }
    this.frame = requestAnimationFrame((t) => this.tick(t));
  }
}

if (!customElements.get('s-nav-marquee')) customElements.define('s-nav-marquee', SNavMarquee);
